import React from 'react';
import styled from 'styled-components';
import { KeyboardDetectionProvider, spacer24, screenMdMin, Type, screenSmMax, spacer16, screenXxsMax, createBasicColorSet, cssColorSet, ButtonPrimary, IconCheckAltFill, spacer8, spacer32, screenMdMax, spacer48, white, black, } from '@spotify-internal/encore-web';
import { ContentfulImage, DELAYS, ImageAspectBox1x1, fadeInAnimation, Grid, withAnimationContext, IconNumberFill, TWO_COLUMN_MAX_WIDTH, } from '../../ui';
import { fixHref, formatText } from '../../ui/utils';
import { altoFontSize } from '@spotify-internal/encore-foundation/desktop';
const ChecklistModuleWrapper = styled(Grid) `
  ${({ backgroundColor, textColor }) => cssColorSet(createBasicColorSet(backgroundColor, textColor))}
  padding-top: ${spacer24};
  padding-bottom: ${spacer24};
  height: auto;
  @media (max-width: ${screenSmMax}) {
    padding-bottom: ${spacer48};
  }
`;
const InnerText = styled.div `
  height: auto;
  align-self: center;

  @media (max-width: ${screenXxsMax}) {
    width: 100%;
    justify-content: center;
  }
`;
const ImageContainer = styled(ImageAspectBox1x1) `
  width: 100%;

  @media (max-width: ${screenSmMax}) {
    margin-bottom: ${spacer24};
  }
`;
const EyeBrowWrapper = withAnimationContext(styled.div `
  ${fadeInAnimation(DELAYS.FIRST)};
`);
const EyebrowText = styled(Type.p).attrs({
    variant: 'ballad',
}) `
  padding-bottom: ${spacer8};
`;
const EyeBrowNumber = styled.div `
  padding-bottom: ${spacer16};
`;
const Headline = withAnimationContext(styled(Type.h1).attrs({
    paddingBottom: '0.67em',
    variant: 'altoBrio',
    weight: 'bold',
}) `
    ${fadeInAnimation(DELAYS.SECOND)};
    padding-bottom: 0px;
    max-width: 684px;
    line-height: normal;

    @media (max-width: ${screenMdMax}) {
      font-size: ${altoFontSize};
    }

    @media (min-width: ${screenMdMin}) {
      text-align: left;
    }
  `);
const Description = withAnimationContext(styled(Type.p).attrs({
    variant: 'ballad',
}) `
    ${fadeInAnimation(DELAYS.THIRD)};
    padding-top: ${spacer16};
    padding-bottom: ${spacer16};
  `);
const ChecklistIconWrapper = styled.div `
  display: flex;
  height: 1.3em;
  align-items: center;
  color: ${({ color }) => color};
  fill: ${({ color }) => color};
`;
const ChecklistWrapper = withAnimationContext(styled.div `
  ${fadeInAnimation(DELAYS.FOURTH)};
`);
const ChecklistItem = styled.li `
  display: flex;
  padding-bottom: ${({ paddingBottom }) => paddingBottom};
`;
const CTAContainer = withAnimationContext(styled.div `
    ${fadeInAnimation(DELAYS.FIFTH)};
    display: flex;
    padding-top: ${spacer32};

    @media (max-width: ${screenXxsMax}) {
      width: 100%;
      justify-content: center;
    }
  `);
const ImageBox = styled.div `
  grid-column: ${({ layout }) => (layout === 'left' ? '1 / 6' : '8 / 13')};
  order: ${({ layout }) => (layout === 'left' ? '1' : '2')};
  align-self: center;
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    grid-column: 1 / -1;
    order: 1;
  }
`;
const TextBox = styled.div `
  display: flex;
  justify-content: flex-start;
  grid-column: ${({ layout }) => (layout === 'left' ? '7 / 12' : '2 / 7')};
  order: ${({ layout }) => (layout === 'left' ? '2' : '1')};

  @media (max-width: ${screenMdMax}) {
    grid-column: ${({ layout }) => (layout === 'left' ? '7 / 13' : '1 / 7')};
  }
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    grid-column: 1 / -1;
    order: 2;
  }
`;
export const ChecklistModule = React.forwardRef(({ eyebrowNumber, eyebrowText, headline, description, checklistItems, isNumberedChecklist, image, ctaLink, ctaText, colors = {
    backgroundColor: black,
    textColor: white,
    iconColor: null,
}, layout = 'left', btnComponent, }, ref) => {
    return (React.createElement(KeyboardDetectionProvider, null,
        React.createElement(ChecklistModuleWrapper, { backgroundColor: colors === null || colors === void 0 ? void 0 : colors.backgroundColor, textColor: colors === null || colors === void 0 ? void 0 : colors.textColor },
            React.createElement(ImageBox, { layout: layout }, image && (React.createElement(ImageContainer, null,
                React.createElement(ContentfulImage, { asset: image })))),
            React.createElement(TextBox, { layout: layout, ref: ref },
                React.createElement(InnerText, null,
                    (eyebrowNumber || eyebrowText) && (React.createElement(EyeBrowWrapper, null, eyebrowText ? (React.createElement(EyebrowText, { "data-testid": "hero-body-headline", dangerouslySetInnerHTML: {
                            __html: formatText(eyebrowText.toUpperCase()),
                        } })) : (React.createElement(EyeBrowNumber, null,
                        React.createElement(IconNumberFill, { number: eyebrowNumber, size: 24, fillColor: colors.textColor }))))),
                    headline && (React.createElement(Headline, { "data-testid": "hero-body-headline", dangerouslySetInnerHTML: { __html: formatText(headline) } })),
                    description && (React.createElement(Description, { dangerouslySetInnerHTML: { __html: formatText(description) } })),
                    checklistItems && (React.createElement(ChecklistWrapper, null, checklistItems.map((item, idx) => {
                        const itemNumber = idx + 1;
                        return (React.createElement(React.Fragment, null,
                            React.createElement(ChecklistItem, { paddingBottom: idx < checklistItems.length - 1
                                    ? spacer16
                                    : undefined },
                                React.createElement(ChecklistIconWrapper, { color: (colors === null || colors === void 0 ? void 0 : colors.iconColor) || colors.textColor }, isNumberedChecklist ? (React.createElement(IconNumberFill, { number: itemNumber, size: 16 })) : (React.createElement(IconCheckAltFill, { iconSize: 16 }))),
                                React.createElement(Type, { style: { paddingLeft: spacer8 }, variant: "ballad", dangerouslySetInnerHTML: {
                                        __html: formatText(item),
                                    } }))));
                    }))),
                    ctaText && ctaLink && (React.createElement(CTAContainer, null,
                        React.createElement(ButtonPrimary, { href: fixHref(ctaLink) || '', UNSAFE_colorSet: createBasicColorSet(colors.textColor, colors.backgroundColor), component: btnComponent }, ctaText))))))));
});
//# sourceMappingURL=index.js.map